import { create } from 'zustand';
import SentryService from '@/services/sentry/Sentry.service';
import {
  EnvironmentConfig,
  EnvironmentName,
  webEnvironment,
  getEventConfiguration,
  EventData,
} from '@/shared/config';
import { IS_WEB } from '@/shared/constants/utility';
import {
  StorageKeys,
  StorageService,
} from '@/services/storage/Storage.service';
import {
  localEnvironment,
  productionEnvironment,
  stagingEnvironment,
} from '@/shared/config/ios';

interface ConfigState {
  environment: EnvironmentConfig;
  setEnvironment: (environmentName: EnvironmentName) => void;
  loadEventConfiguration: () => Promise<EventData>;
  isDevMenuOpen: boolean;
  setIsDevMenuOpen: (isOpen: boolean) => void;
  localIP: string;
  setLocalIP: (ip: string) => Promise<void>;
}

export const environmentMap: Record<EnvironmentName, EnvironmentConfig> = {
  local: localEnvironment,
  staging: stagingEnvironment,
  production: productionEnvironment,
  web: webEnvironment,
};

const useConfigStore = create<ConfigState>((set, get) => ({
  environment: IS_WEB ? webEnvironment : environmentMap['production'],
  setEnvironment: async (environmentName: EnvironmentName) => {
    __DEV__ && console.log(`Loading environment: ${environmentName}`);

    const newEnvironment = environmentMap[environmentName];
    const localIP = get().localIP;

    SentryService.resetSentry(newEnvironment);

    set(() => {
      const updatedEnvironment =
        environmentName === 'local' && localIP
          ? {
              ...newEnvironment,
              server: {
                ...newEnvironment.server,
                baseUrl: `http://${localIP}:8080`,
              },
            }
          : newEnvironment;

      return { environment: updatedEnvironment };
    });

    await StorageService.setItem(StorageKeys.ENVIRONMENT_NAME, environmentName);
  },
  loadEventConfiguration: async () => {
    try {
      const eventData = await getEventConfiguration();
      __DEV__ && console.log('Loaded dynamic event configuration:', eventData);
      return eventData;
    } catch (error) {
      SentryService.captureError('Failed to load event configuration:', error);
      // Return fallback configuration
      return {
        inPersonEventSessions: true,
        eventPeriods: [
          {
            startDate: '2025-09-19',
            endDate: '2025-09-21',
          },
          {
            startDate: '2025-09-26',
            endDate: '2025-09-28',
          },
        ],
      };
    }
  },
  isDevMenuOpen: false,
  setIsDevMenuOpen: (isOpen: boolean) => set({ isDevMenuOpen: isOpen }),
  localIP: 'localhost',
  setLocalIP: async (ip: string) => {
    set({ localIP: ip });
    await StorageService.setItem(StorageKeys.LOCAL_IP, ip);
  },
}));

export default useConfigStore;
